/**
 * A sheet's header and footer surfaces (`SheetSurfaces`): the system rows a
 * sheet builds for itself, swapped in as one structural batch
 * (`replaceSystemRows`, applying `planSystemRows`) and reparented onto a
 * persisted or authored surface anchor when one exists; the anchor lookup;
 * and the server-backed surface row a row added to a surface is parented
 * under (`ensureServerBacked`, `addToSurface`).
 *
 * It is the sheet's one reader of surface anchors (`SurfaceAnchor`, which
 * the row index's header / footer lookup also takes from here). The rows,
 * the tree and the backend stay the sheet's, handed in through three narrow
 * roles (`SheetSurfaces.Rows`, `.Structure`, `.Persist`). It does not name
 * `Sheet`, so it sits below it.
 */

import { Uuid } from "@tento-core/uuid";
import type { Option } from "../core/option.ts";
import { Json } from "../core/json.ts";
import { RowId } from "../ids/row-id.ts";
import type { SheetId } from "../ids/sheet-id.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import type { Backend, SurfaceKind } from "../backends/rows-backend.ts";
import type { ModelRow } from "../rows/model-row.ts";
import type { SystemRowDef } from "../rows/system-rows.ts";
import { SurfaceAnchor } from "../render/surface-anchor.ts";
import type { RawRow } from "../wire/raw-sheet-wire.ts";
import type { AddRowSpec, SheetStores } from "./rows-sheet.ts";
import { planSystemRows } from "./sheet-system-rows-plan.ts";

/** Types that go with `SheetSurfaces`: its three host roles. */
export namespace SheetSurfaces {
  /** The sheet's rows and tree. */
  export interface Rows {
    /** Every row the sheet holds, in insertion order. */
    rows(): readonly ModelRow[];
    /** The row `id` names, or `null` when it is not this sheet's. */
    row(id: RowLocalId): Option<ModelRow>;
    tree(): SheetStores["tree"];
    /** The local id of the raw row id `rawId` (`RowLocalId.Registry.resolveFromRawId`). */
    resolveRawId(rawId: string): RowLocalId;
    /** The sheet's header / footer row as its row index answers it (`peekHeaderRow` / `peekFooterRow`). */
    surfaceRow(kind: SurfaceKind): Option<ModelRow>;
  }

  /** The sheet's structural edits. */
  export interface Structure {
    addRow(spec: AddRowSpec): ModelRow;
    /** Drop `id` and its subtree from the sheet. */
    dropRowSubtree(id: RowLocalId): void;
    /** Run `fn` with structural-change emits coalesced to one on drain. */
    withDeferredStructure<T>(fn: () => T): T;
  }

  /** Where a server-backed surface row is created. */
  export interface Persist {
    readonly backend: Pick<Backend, "addRow">;
    sheetId(): Option<SheetId>;
  }
}

/** A row's surface anchor (`SurfaceAnchor.of`): the row index's header / footer lookup reads it here. */
export function surfaceAnchorOf(row: ModelRow): Option<SurfaceAnchor.Anchor> {
  return SurfaceAnchor.of(row);
}

/** A sheet's header and footer surfaces (see the module note). */
export class SheetSurfaces {
  readonly #rows: SheetSurfaces.Rows;
  readonly #structure: SheetSurfaces.Structure;
  readonly #persist: SheetSurfaces.Persist;

  constructor(rows: SheetSurfaces.Rows, structure: SheetSurfaces.Structure, persist: SheetSurfaces.Persist) {
    this.#rows = rows;
    this.#structure = structure;
    this.#persist = persist;
  }

  /**
   * Replace every existing system row with `rows`. Atomic-ish —
   * existing system rows (those whose id is `system(...)`) are
   * deleted in one pass, then each new def is appended via the
   * normal `addRow` path.
   *
   * Callers build `rows` with `SystemRows.build(config)` (pure;
   * see `../rows/system-rows.ts`). The SDK-side wrapper reads
   * preferences into the config; this method is purely
   * mechanical — given the list, apply it.
   *
   * Child relationships are applied from each def's `children` list.
   */
  replaceSystemRows(rows: readonly SystemRowDef[]): void {
    // Coalesce the structural-change storm: the delete-all + re-add below would
    // otherwise emit one `SheetStructureChanged` per tree mutation, each fanning
    // out synchronously to a full `recomputeComputedSources` + the UI effect set
    // — the multi-second rapid-switch freeze. `withDeferredStructure` collapses
    // them to ONE emit on drain.
    this.#structure.withDeferredStructure(() => this.#replaceSystemRowsInner(rows));
  }

  #replaceSystemRowsInner(rows: readonly SystemRowDef[]): void {
    const tree = this.#rows.tree();
    // Delete existing top-level system subtrees first. Walk the row list
    // to a snapshot because deletion mutates it.
    const existing = this.#rows.rows()
      .filter((row) => row.id.key.isSystem())
      .map((row) => row.id);
    const existingSet = new Set(existing);
    for (const id of existing) {
      const parent = tree.parent(id);
      if (parent != null && existingSet.has(parent)) continue;
      this.#structure.dropRowSubtree(id);
    }

    // Detect existing surface anchor rows at top level —
    // server-backed surface-containers (post-Phase-D / C2
    // migration) OR authored surface region rows (template
    // `surface[header|footer]`). When present, system rows
    // reparent into them instead of using the synthetic
    // surface-container the factory produced. This keeps the
    // sticky surface a single row whose children include both
    // the synthetic system rows AND any persisted/authored rows.
    const headerAnchorId = this.#anchorRowId("header");
    const footerAnchorId = this.#anchorRowId("footer");

    // The plan (`planSystemRows`): a synthetic surface-container whose
    // anchor exists is dropped and its children reparent onto the anchor;
    // every other row is added parent before child.
    const plan = planSystemRows(rows, { header: headerAnchorId, footer: footerAnchorId });
    for (const { def, parentId } of plan.adds) this.#addSystemRowDef(def, parentId);

    // System rows reparented onto a surface anchor are inserted at
    // the END (default `addRow` behaviour). Re-order them to the
    // FRONT of the anchor's children so the system row content
    // (calendar-system labels, etc.) renders on top, with
    // previously-frozen / user-pinned rows below.
    let frontInsertIdx = 0;
    for (const childIdStr of plan.reparented) {
      const localChildId = this.#rows.resolveRawId(childIdStr);
      const child = this.#rows.row(localChildId);
      if (!child) continue;
      const parent = tree.parent(localChildId);
      tree.reparent(localChildId, parent, frontInsertIdx);
      frontInsertIdx += 1;
    }

    if (plan.cycleAt != null) {
      throw new Error(
        `Sheet.replaceSystemRows: system rows contain a cycle at "${plan.cycleAt}".`,
      );
    }
  }

  /**
   * Locate a top-level surface anchor row for `kind` — a
   * server-backed surface-container (`meta.surface`) or an authored
   * surface region row (`attributes.surface`); legacy synthetic
   * placeholders don't count. Returns the raw id string (used as
   * `addRow`'s `parentId` parameter) or `null` when no such row
   * exists. Server-backed wins over authored when both exist.
   * The synthetic surface-container created by `replaceSystemRows`
   * is excluded via the `isSystem()` check — we only want
   * persisted/authored, non-system instances here so the
   * "skip + reparent" path doesn't loop on its own output.
   */
  #anchorRowId(kind: SurfaceKind): Option<string> {
    let authored: Option<string> = null;
    for (const id of this.#rows.tree().roots()) {
      const row = this.#rows.row(id);
      if (!row) continue;
      // Skip the synthetic surface-container we just deleted.
      if (row.id.key.isSystem()) continue;
      const anchor = SurfaceAnchor.of(row);
      if (!anchor || anchor.kind !== kind) continue;
      if (anchor.source === "server") {
        return RowId.fromLocalId(row.id).toString();
      }
      if (anchor.source === "authored") {
        authored ??= RowId.fromLocalId(row.id).toString();
      }
    }
    return authored;
  }

  #addSystemRowDef(def: SystemRowDef, parentId: Option<string>): void {
    this.#structure.addRow({
      id: RowNetworkKey.of(def.id),
      type: def.type,
      ...(parentId !== null ? { parentId: RowNetworkKey.fromWire(parentId) } : {}),
      ...(def.label !== undefined && def.label !== null
        ? { label: def.label }
        : {}),
      ...(def.lookupKey !== undefined && def.lookupKey !== null
        ? { lookupKey: def.lookupKey }
        : {}),
      ...(def.formula !== undefined && def.formula !== null
        ? { formula: def.formula }
        : {}),
      ...(def.timeScale !== undefined && def.timeScale !== null
        ? { timeScale: def.timeScale }
        : {}),
      ...(Json.object(def.metadata) != null
        ? { meta: Json.object(def.metadata) ?? {} }
        : {}),
      ...(def.heightPx !== undefined && def.heightPx !== null
        ? { heightPx: def.heightPx }
        : {}),
      ...(Json.object(def.attributes) != null
        ? { attributes: Json.object(def.attributes) ?? {} }
        : {}),
    });
  }

  /**
   * Add a row as a child of the unique `kind` surface. If the
   * surface is still synthetic (no server-backed
   * `surface-container` row with `meta.surface === kind` yet),
   * the SDK first creates one via
   * `Backend.addRow(..., surface: kind)`, then parents the
   * spec under it.
   *
   * The synthetic placeholder and the server-backed instance are
   * two forms of the same logical row; `peekHeaderRow()` returns the
   * server-backed row after creation, the synthetic is suppressed
   * by the row index's tie-break (`SheetRowIndex`).
   *
   * Returns the newly added row (the spec's row, not the surface
   * row).
   */
  async addToSurface(kind: SurfaceKind, spec: AddRowSpec): Promise<ModelRow> {
    const surface = await this.ensureServerBacked(kind);
    return this.#structure.addRow({ ...spec, parentId: RowNetworkKey.of(surface.id) });
  }

  /**
   * Make sure a server-backed surface row exists for `kind`
   * (`Sheet.ensureServerBackedSurface`). Returns the existing one if
   * present; otherwise issues a `Backend.addRow(..., surface: kind)`
   * write, mirrors the resulting row into the local sheet, and returns it.
   *
   * The local mirror uses the same UUID the client sent — the
   * `/api/rows` POST endpoint accepts the client-picked id, so
   * the local mirror's id matches what gets persisted.
   */
  async ensureServerBacked(kind: SurfaceKind): Promise<ModelRow> {
    const existing = this.#rows.surfaceRow(kind);
    const existingSource = existing ? SurfaceAnchor.of(existing)?.source : null;
    if (
      existing &&
      (existingSource === "server" || existingSource === "authored")
    ) {
      // Already a real anchor: server-backed surface-container (the
      // D3 tie-break promotes it ahead of any legacy synthetic) or
      // an authored surface region row (template
      // `surface[header|footer]`). Parent under it directly —
      // creating a server-backed container alongside would
      // duplicate the surface.
      return existing;
    }

    // Materialize the server-backed surface row. Wire type is
    // `surface-container` — an inert, intrinsically transparent
    // row (zero cellHeight, no plugin); the server stamps
    // `metadata.surface` on creation. Row id is a fresh UUID
    // the server takes verbatim, so the local mirror's id
    // matches what gets persisted.
    const surfaceId = Uuid.make();
    const surfaceRow: RawRow = {
      id: RowNetworkKey.fromWire(surfaceId),
      type: "surface-container",
      meta: { surface: kind },
    };
    await this.#persist.backend.addRow(surfaceRow, this.#persist.sheetId(), null, null, kind);

    // Mirror into local tree so subsequent finder lookups + the
    // body-view-tree resolver pick up the server-backed row.
    return this.#structure.addRow(surfaceRow);
  }
}
